import { apiClient } from '../apiClient';

/**
 * Helper to extract company slug from current URL path
 * CRITICAL: Returns empty string if company not found to prevent malformed URLs
 */
const getCompanySlug = (): string => {
  if (typeof window === 'undefined') return '';
  const parts = window.location.pathname.split('/').filter(Boolean);
  const company = parts[0] || '';
  
  // CRITICAL FIX: Validate company slug to prevent empty/malformed URLs
  // Empty company would result in /api//rpc/managers/list which causes 404
  if (!company || company === 'undefined' || company.trim() === '') {
    console.warn('[rpcManagers] Company slug is empty or invalid:', {
      pathname: window.location.pathname,
      parts,
    });
    return '';
  }
  
  return company;
};

/**
 * Build RPC endpoint URL
 * CRITICAL: Validates company before building URL to prevent 404s
 */
const buildUrl = (path: string): string => {
  const company = getCompanySlug();
  
  // CRITICAL FIX: Prevent API calls with empty company (would cause 404)
  if (!company) {
    const error = new Error('Company slug is required but not available. Please ensure you are on a valid company page.');
    console.error('[rpcManagers] buildUrl failed:', error.message, {
      pathname: window.location.pathname,
    });
    throw error; // Throw error to prevent malformed request
  }
  
  return `/api/${company}/rpc/managers${path}`;
};

/**
 * RPC client for manager-related operations
 * All endpoints return { ok: true, data } or { ok: false, error }
 */
const rpcManagers = {
  /**
   * List all managers
   * @param options - Pagination and filtering options
   */
  list(options?: { page?: number; limit?: number; isBlocked?: boolean; isArchived?: boolean; search?: string }) {
    const companySlug = getCompanySlug();
    const payload = {
      page: options?.page || 1,
      limit: options?.limit || 20,
      ...(options?.isBlocked !== undefined && { isBlocked: options.isBlocked }),
      ...(options?.isArchived !== undefined && { isArchived: options.isArchived }),
      ...(options?.search && { search: options.search }),
    };
    
    return apiClient.post(buildUrl('/list'), payload).catch((error) => {
      // Error handling
      throw error; // Re-throw to let React Query handle it
    });
  },

  /**
   * Get manager by ID
   */
  getById(managerId: string) {
    return apiClient.post(buildUrl('/get-by-id'), { managerId });
  },

  /**
   * Create new manager
   */
  create(payload: {
    fullName: string;
    email: string;
    password: string;
    phone?: string;
    permissions?: string[];
  }) {
    return apiClient.post(buildUrl('/create'), payload);
  },

  /**
   * Update manager
   */
  update(
    managerId: string,
    payload: {
      fullName?: string;
      email?: string;
      phone?: string;
      permissions?: string[];
    }
  ) {
    return apiClient.post(buildUrl('/update'), { managerId, ...payload });
  },

  /**
   * Block/unblock manager
   */
  block(managerId: string, isBlocked: boolean) {
    return apiClient.post(buildUrl('/block'), { managerId, isBlocked });
  },

  /**
   * Archive/unarchive manager
   */
  archive(managerId: string, isArchived: boolean) {
    return apiClient.post(buildUrl('/archive'), { managerId, isArchived });
  },

  /**
   * Change manager password
   */
  changePassword(managerId: string, newPassword: string) {
    return apiClient.post(buildUrl('/change-password'), { managerId, newPassword });
  },

  /**
   * Convert admin to manager with manager permissions
   */
  convertAdminToManager(targetUserId: string) {
    return apiClient.post(buildUrl('/convert-admin-to-manager'), { targetUserId });
  },

  /**
   * Get current manager profile
   */
  me() {
    return apiClient.post(buildUrl('/me'), {});
  },

  /**
   * List manager's clients
   */
  listClients() {
    return apiClient.post(buildUrl('/clients/list'), {});
  },

  /**
   * Get a client by ID (manager scope)
   */
  getClientById(clientId: string) {
    return apiClient.post(buildUrl('/clients/get-by-id'), { clientId });
  },

  /**
   * Update a client (manager scope)
   */
  updateClient(clientId: string, payload: any) {
    return apiClient.post(buildUrl('/clients/update'), { clientId, payload });
  },

  /**
   * List notes for a client (manager scope)
   */
  listClientNotes(clientId: string) {
    return apiClient.post(buildUrl('/clients/notes/list'), { clientId });
  },

  /**
   * Create a note for a client (manager scope)
   */
  createClientNote(clientId: string, text: string) {
    return apiClient.post(buildUrl('/clients/notes/create'), { clientId, text });
  },

  /**
   * Update a note for a client (manager scope)
   */
  updateClientNote(clientId: string, noteIdx: number, text: string) {
    return apiClient.post(buildUrl('/clients/notes/update'), { clientId, noteIdx, text });
  },

  /**
   * List tasks for a client (manager scope)
   */
  listClientTasks(clientId: string) {
    return apiClient.post(buildUrl('/clients/tasks/list'), { clientId });
  },

  /**
   * List all managers (for current admin)
   */
  listManagers() {
    return apiClient.post(buildUrl('/managers/list'), {});
  },

  /**
   * List manager's tasks
   */
  listTasks(params?: { status?: string; month?: string; clientId?: string; q?: string }) {
    return apiClient.post(buildUrl('/tasks/list'), params || {});
  },

  /**
   * Get a task by ID
   */
  getTaskById(taskId: string) {
    return apiClient.post(buildUrl('/tasks/get-by-id'), { taskId });
  },

  /**
   * Update a task
   */
  updateTask(taskId: string, payload: any) {
    return apiClient.post(buildUrl('/tasks/update'), { taskId, ...payload });
  },

  /**
   * Complete a task
   */
  completeTask(taskId: string) {
    return apiClient.post(buildUrl('/tasks/complete'), { taskId });
  },

  /**
   * List notes for a task
   */
  listTaskNotes(taskId: string) {
    return apiClient.post(buildUrl('/tasks/notes/list'), { taskId });
  },

  /**
   * Create a note for a task
   */
  createTaskNote(taskId: string, body: string, visibility?: 'all' | 'admin' | 'manager') {
    return apiClient.post(buildUrl('/tasks/notes/create'), { taskId, body, visibility });
  },

  /**
   * List activity for a task
   */
  listTaskActivity(taskId: string) {
    return apiClient.post(buildUrl('/tasks/activity/list'), { taskId });
  },

  /**
   * Get manager statistics
   */
  getStats() {
    return apiClient.post(buildUrl('/stats'), {});
  },
};

export default rpcManagers;

